import { useCallback, useEffect, useState } from 'react';
import {
  AUTHORIZATION_KIND_LABEL,
  AUTHORIZATION_STATUS_LABEL,
  getEvidenceUrl,
  listAuthorizations,
  reviewAuthorization,
  type AuthorizationStatus,
  type AuthorizationWithWorker,
} from '../../api/authorizations';
import FilaTarjeta from '../../components/FilaTarjeta';
import { useAuth } from '../../components/AuthProvider';
import { puedeEditar } from '../../domain/permissions';

const dmy = (d: string) => d.split('-').reverse().join('/');

const STATUS_CLS: Record<AuthorizationStatus, string> = {
  pendiente: 'bg-amber-100 text-amber-800',
  aprobado: 'bg-green-100 text-green-800',
  rechazado: 'bg-red-100 text-red-800',
};

type FiltroEstado = AuthorizationStatus | 'todas';

const FILTROS: { value: FiltroEstado; label: string }[] = [
  { value: 'pendiente', label: 'Pendientes' },
  { value: 'aprobado', label: 'Aprobadas' },
  { value: 'rechazado', label: 'Rechazadas' },
  { value: 'todas', label: 'Todas' },
];

export default function Autorizaciones() {
  const [estado, setEstado] = useState<FiltroEstado>('pendiente');
  const { roles } = useAuth();
  /**
   * Decidir sobre el permiso de alguien es del coordinador y del jefe; `datos` lo LEE (10-set).
   *
   * Y es la pantalla que más le cambia el trabajo: un permiso aprobado es la diferencia entre una
   * falta y una ausencia justificada, así que sin verla estaba revisando faltas sin saber cuáles
   * tenían papel. Pero aprobar o rechazar no es revisar, y el servidor tampoco se lo permite
   * (`review_authorization` exige admin o coordinador), así que los dos botones no se le dibujan.
   */
  const puedeDecidir = puedeEditar('autorizaciones', roles);
  const [rows, setRows] = useState<AuthorizationWithWorker[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [accion, setAccion] = useState<
    { row: AuthorizationWithWorker; nuevo: 'aprobado' | 'rechazado' } | null
  >(null);

  const recargar = useCallback(() => {
    setLoading(true);
    listAuthorizations({ status: estado })
      .then(setRows)
      .catch((e) => setMessage(e instanceof Error ? e.message : 'Error al cargar solicitudes.'))
      .finally(() => setLoading(false));
  }, [estado]);

  useEffect(recargar, [recargar]);

  async function verEvidencia(path: string) {
    try {
      window.open(await getEvidenceUrl(path), '_blank');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'No se pudo abrir la evidencia.');
    }
  }

  return (
    <div className="p-6">
      <h1 className="mb-1 text-2xl font-semibold">Autorizaciones</h1>
      <p className="mb-4 text-sm text-gray-500">
        Solicitudes de justificación de tardanzas, faltas y permisos.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          Estado
          <select
            className="rounded border px-2 py-1"
            value={estado}
            onChange={(e) => setEstado(e.target.value as FiltroEstado)}
          >
            {FILTROS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        <button onClick={recargar} className="rounded border px-3 py-1 text-sm">
          Actualizar
        </button>
        {message && <span className="text-sm text-gray-700">{message}</span>}
      </div>

      {/* En celular: una tarjeta por solicitud (sin desplazamiento horizontal). */}
      <div className="space-y-2 lg:hidden">
        {rows.map((r) => (
          <FilaTarjeta
            key={r.id}
            titulo={r.worker ? `${r.worker.first_names} ${r.worker.last_names}` : '—'}
            estados={
              <span className={`rounded px-2 py-0.5 text-xs ${STATUS_CLS[r.status]}`}>
                {AUTHORIZATION_STATUS_LABEL[r.status]}
              </span>
            }
            campos={[
              { etiqueta: 'DNI', valor: r.worker?.dni },
              { etiqueta: 'Tipo', valor: AUTHORIZATION_KIND_LABEL[r.kind] },
              { etiqueta: 'Fecha del evento', valor: dmy(r.event_date) },
              { etiqueta: 'Motivo', valor: r.reason },
              { etiqueta: 'Nota', valor: r.review_note },
            ]}
            acciones={
              <>
                {r.evidence_path && (
                  <button
                    onClick={() => verEvidencia(r.evidence_path!)}
                    className="text-blue-600 underline"
                  >
                    Ver foto
                  </button>
                )}
                {puedeDecidir && r.status === 'pendiente' && (
                  <>
                    <button
                      onClick={() => setAccion({ row: r, nuevo: 'aprobado' })}
                      className="rounded bg-green-600 px-3 py-1 text-xs text-white"
                    >
                      Aprobar
                    </button>
                    <button
                      onClick={() => setAccion({ row: r, nuevo: 'rechazado' })}
                      className="rounded bg-red-600 px-3 py-1 text-xs text-white"
                    >
                      Rechazar
                    </button>
                  </>
                )}
              </>
            }
          />
        ))}
        {!loading && rows.length === 0 && (
          <p className="rounded border bg-white p-4 text-center text-sm text-gray-500">
            No hay solicitudes con ese estado.
          </p>
        )}
        {loading && (
          <p className="rounded border bg-white p-4 text-center text-sm text-gray-500">Cargando…</p>
        )}
      </div>

      <div className="hidden overflow-x-auto lg:block">
        <table className="min-w-full border text-sm">
          <thead className="bg-gray-100">
            <tr className="text-left">
              <th className="p-2">Trabajador</th>
              <th className="p-2">DNI</th>
              <th className="p-2">Tipo</th>
              <th className="p-2">Fecha del evento</th>
              <th className="p-2">Motivo</th>
              <th className="p-2">Evidencia</th>
              <th className="p-2">Estado</th>
              <th className="p-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t align-top">
                <td className="p-2">
                  {r.worker ? `${r.worker.first_names} ${r.worker.last_names}` : '—'}
                </td>
                <td className="p-2">{r.worker?.dni ?? '—'}</td>
                <td className="p-2">{AUTHORIZATION_KIND_LABEL[r.kind]}</td>
                <td className="p-2">{dmy(r.event_date)}</td>
                <td className="max-w-xs p-2">
                  <div>{r.reason}</div>
                  {r.review_note && (
                    <div className="mt-1 text-xs text-gray-500">Nota: {r.review_note}</div>
                  )}
                </td>
                <td className="p-2">
                  {r.evidence_path ? (
                    <button
                      onClick={() => verEvidencia(r.evidence_path!)}
                      className="text-blue-600 underline"
                    >
                      Ver foto
                    </button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="p-2">
                  <span className={`rounded px-2 py-0.5 text-xs ${STATUS_CLS[r.status]}`}>
                    {AUTHORIZATION_STATUS_LABEL[r.status]}
                  </span>
                </td>
                <td className="p-2">
                  {puedeDecidir && r.status === 'pendiente' ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setAccion({ row: r, nuevo: 'aprobado' })}
                        className="rounded bg-green-600 px-3 py-1 text-xs text-white"
                      >
                        Aprobar
                      </button>
                      <button
                        onClick={() => setAccion({ row: r, nuevo: 'rechazado' })}
                        className="rounded bg-red-600 px-3 py-1 text-xs text-white"
                      >
                        Rechazar
                      </button>
                    </div>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="p-4 text-center text-gray-500">
                  No hay solicitudes con ese estado.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={8} className="p-4 text-center text-gray-500">
                  Cargando…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {accion && (
        <ModalRevision
          row={accion.row}
          nuevo={accion.nuevo}
          onClose={() => setAccion(null)}
          onDone={(msg) => {
            setAccion(null);
            setMessage(msg);
            recargar();
          }}
        />
      )}
    </div>
  );
}

function ModalRevision({
  row,
  nuevo,
  onClose,
  onDone,
}: {
  row: AuthorizationWithWorker;
  nuevo: 'aprobado' | 'rechazado';
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const rechaza = nuevo === 'rechazado';

  async function confirmar() {
    if (rechaza && note.trim() === '') {
      setError('Escribe el motivo del rechazo: el trabajador lo verá en su celular.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await reviewAuthorization(row.id, nuevo, note.trim());
      onDone(rechaza ? 'Solicitud rechazada.' : 'Solicitud aprobada.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo registrar la revisión.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded bg-white p-4 shadow-lg">
        <h2 className="mb-1 font-semibold">
          {rechaza ? 'Rechazar solicitud' : 'Aprobar solicitud'}
        </h2>
        <p className="mb-3 text-sm text-gray-600">
          {row.worker ? `${row.worker.first_names} ${row.worker.last_names}` : 'Trabajador'} —{' '}
          {AUTHORIZATION_KIND_LABEL[row.kind]} del {dmy(row.event_date)}
        </p>
        <p className="mb-1 text-sm">
          Comentario {rechaza ? '(obligatorio)' : '(opcional)'}:
        </p>
        <textarea
          className="w-full rounded border p-2 text-sm"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="rounded border px-3 py-1 text-sm">
            Cancelar
          </button>
          <button
            onClick={confirmar}
            disabled={busy}
            className={`rounded px-3 py-1 text-sm text-white disabled:opacity-50 ${
              rechaza ? 'bg-red-600' : 'bg-green-600'
            }`}
          >
            {busy ? 'Guardando…' : rechaza ? 'Rechazar' : 'Aprobar'}
          </button>
        </div>
      </div>
    </div>
  );
}
